import { Facebook, Instagram, Mail, MapPin, MessageCircle } from "lucide-react";
import { LOGO_FULL } from "@/lib/api";
import { useSite, waLink, bookingUrl } from "@/context/SiteContext";

export const Footer = () => {
  const { settings } = useSite();

  return (
    <footer data-testid="site-footer" className="bg-ink text-sand pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid md:grid-cols-12 gap-10 pb-12 border-b border-sand/15">
          <div className="md:col-span-5">
            <img src={LOGO_FULL} alt="Mahadinusa Lembongan logo" className="w-52 -ml-2" data-testid="footer-logo" />
            <p className="mt-5 text-sm text-sand/60 leading-relaxed max-w-sm">
              A boutique island stay in Jungutbatu, Nusa Lembongan — garden rooms, a flowering pool and an island
              host for boats, scooters and slow days.
            </p>
            <p className="mt-6 text-[10px] font-mono uppercase tracking-[0.25em] text-sand/50">Connect with us</p>
            <div className="mt-3 flex gap-3">
              <a
                href={waLink(settings)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-link"
                className="h-10 w-10 grid place-items-center rounded-full border border-sand/25 hover:bg-azure hover:border-azure transition-colors"
                aria-label="WhatsApp"
              >
                <MessageCircle size={16} />
              </a>
              <a
                href={settings?.instagram || "https://www.instagram.com/mahadinusa_lembongan/"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="h-10 w-10 grid place-items-center rounded-full border border-sand/25 hover:bg-azure hover:border-azure transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href={settings?.facebook || "https://www.facebook.com/profile.php?id=61558035414036"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook-link"
                className="h-10 w-10 grid place-items-center rounded-full border border-sand/25 hover:bg-azure hover:border-azure transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={16} />
              </a>
              {settings?.email && (
                <a
                  href={`mailto:${settings.email}`}
                  data-testid="footer-email-link"
                  className="h-10 w-10 grid place-items-center rounded-full border border-sand/25 hover:bg-azure hover:border-azure transition-colors"
                  aria-label="Email"
                >
                  <Mail size={16} />
                </a>
              )}
              {settings?.maps_url && (
                <a
                  href={settings.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-maps-link"
                  className="h-10 w-10 grid place-items-center rounded-full border border-sand/25 hover:bg-azure hover:border-azure transition-colors"
                  aria-label="Google Maps"
                >
                  <MapPin size={16} />
                </a>
              )}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-sand/50 mb-4">Explore</p>
            <ul className="space-y-2.5 text-sm text-sand/75">
              <li><a href="#story" data-testid="footer-link-story" className="hover:text-sand transition-colors">Our Story</a></li>
              <li><a href="#rooms" data-testid="footer-link-rooms" className="hover:text-sand transition-colors">Rooms & Rates</a></li>
              <li><a href="#journey" data-testid="footer-link-journey" className="hover:text-sand transition-colors">Journey & Fast Boats</a></li>
              <li><a href="#journal" data-testid="footer-link-journal" className="hover:text-sand transition-colors">Journal</a></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-sand/50 mb-4">Reserve</p>
            <p className="text-sm text-sand/75 leading-relaxed">{settings?.address || "Jungutbatu, Nusa Lembongan, Bali"}</p>
            <a
              href={bookingUrl(settings)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reserve-now-footer-button"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold text-ink text-sm font-medium px-6 py-3 transition-all hover:bg-sand"
            >
              Reserve Now
            </a>
          </div>
        </div>

        <div className="pt-7 flex flex-wrap items-center justify-between gap-4 text-[11px] font-mono text-sand/40 tracking-wide">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Mahadinusa Lembongan · Nusa Lembongan, Bali</p>
          <div className="flex items-center gap-4">
            <a
              href="https://armellabali.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-armella-link"
              className="hover:text-sand/80 transition-colors"
            >
              Member of Armella Bali
            </a>
            <a href="/admin/login" data-testid="footer-admin-link" className="hover:text-sand/70 transition-colors">Admin</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
